import { footerCopy, footerLinks } from '@/common/copy/footer';
import { UnifiedIcon } from '@/components/UnifiedIcon';
import { Configs } from '@/config';
import { ThemeToggle } from '@/designSystem/atoms/ThemeToggle';
import { TextLink } from '@/designSystem/atoms/TextLink';
import { GoogleSsoButton } from '@/views/login/GoogleSsoButton';
import { GoogleSsoButtonFallback } from '@/views/login/GoogleSsoButtonFallback';
import { MicrosoftSsoButtonPlaceholder } from '@/views/login/MicrosoftSsoButtonPlaceholder';

export interface LoginFormPanelProps {
  username: string;
  password: string;
  isLoading: boolean;
  loginNotice: string | null;
  errorMessage: string | null;
  showValidationHints: boolean;
  onUsernameChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onGoogleError: (message: string) => void;
  onClearNotice: () => void;
}

/**
 * Right panel: sign-in form, SSO, theme toggle, compact footer — fits above the fold on desktop.
 */
export function LoginFormPanel({
  username,
  password,
  isLoading,
  loginNotice,
  errorMessage,
  showValidationHints,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
  onGoogleError,
  onClearNotice,
}: LoginFormPanelProps) {
  const year = new Date().getFullYear();
  const showPasswordStep = username.trim().length > 0;

  return (
    <div
      data-testid="agents-login-form-panel"
      className="relative flex h-full min-h-0 flex-col overflow-hidden bg-canvas"
    >
      <div className="absolute right-3 top-3 z-10 lg:right-5 lg:top-4">
        <ThemeToggle />
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-center px-5 py-4 sm:px-6 lg:px-10 lg:py-5">
        <section data-testid="agents-login-form-section" className="mx-auto w-full max-w-sm">
          <header data-testid="agents-login-header" className="mb-4 lg:mb-5">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-kc-md bg-accent-muted text-accent lg:hidden">
              <UnifiedIcon name="Robot" size={24} weight="duotone" />
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-primary sm:text-2xl">
              Sign in to Agents
            </h1>
            <p className="mt-1 text-xs text-secondary sm:text-sm">
              Your Kincode account for the agentic platform.
            </p>
          </header>

          {loginNotice ? (
            <p data-testid="agents-login-notice" role="status" className="ds-callout-warning">
              {loginNotice}
            </p>
          ) : null}

          <div data-testid="agents-login-sso-section" className="mb-4 space-y-2">
            {Configs.googleSsoEnabled ? (
              <GoogleSsoButton
                disabled={isLoading}
                onClearNotice={onClearNotice}
                onError={onGoogleError}
              />
            ) : (
              <GoogleSsoButtonFallback />
            )}

            <MicrosoftSsoButtonPlaceholder />

            <div className="flex items-center gap-2 pt-2">
              <span className="h-px flex-1 bg-default" aria-hidden />
              <span className="text-[10px] font-medium uppercase tracking-wide text-muted">
                or continue with email
              </span>
              <span className="h-px flex-1 bg-default" aria-hidden />
            </div>
          </div>

          {errorMessage ? (
            <p data-testid="agents-login-error" role="alert" className="ds-callout-error">
              {errorMessage}
            </p>
          ) : null}

          <form data-testid="agents-login-form" onSubmit={onSubmit} noValidate className="space-y-3">
            <div>
              <label
                htmlFor="agents-login-email"
                className="mb-1 block text-xs font-medium text-secondary sm:text-sm"
              >
                Email
              </label>
              <input
                id="agents-login-email"
                data-testid="agents-login-email"
                type="email"
                autoComplete="username"
                value={username}
                onChange={onUsernameChange}
                className="ds-input py-2 text-sm"
                disabled={isLoading}
                placeholder="you@company.com"
              />
            </div>

            {showPasswordStep ? (
              <>
                <div data-testid="agents-login-password-section">
                  <label
                    htmlFor="agents-login-password"
                    className="mb-1 block text-xs font-medium text-secondary sm:text-sm"
                  >
                    Password
                  </label>
                  <input
                    id="agents-login-password"
                    data-testid="agents-login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={onPasswordChange}
                    className="ds-input py-2 text-sm"
                    disabled={isLoading}
                    placeholder="••••••••"
                  />
                </div>

                {showValidationHints && !username.trim() ? (
                  <p className="text-xs text-[var(--kc-warning-text)]">Email is required.</p>
                ) : null}

                <button
                  type="submit"
                  data-testid="agents-login-submit"
                  disabled={isLoading}
                  className="ds-button-primary w-full py-2.5 text-sm"
                >
                  {isLoading ? (
                    <>
                      <UnifiedIcon name="CircleNotch" className="h-4 w-4 animate-spin" />
                      Signing in…
                    </>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </>
            ) : null}
          </form>
        </section>
      </div>

      <footer
        data-testid="agents-login-footer"
        className="shrink-0 border-t border-default px-5 py-2.5 sm:px-6 lg:px-10"
      >
        <div className="mx-auto flex max-w-sm flex-wrap items-center justify-between gap-x-4 gap-y-1 text-[10px] text-muted sm:text-xs">
          <TextLink href={footerLinks.kincodeHome} variant="muted" data-testid="agents-footer-copyright">
            &copy; {footerCopy.copyright} {year}
          </TextLink>
          <nav
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
            aria-label="Legal and status"
            data-testid="agents-footer-links"
          >
            <TextLink href={footerLinks.termsAndConditions} variant="muted" data-testid="agents-footer-terms">
              Terms
            </TextLink>
            <TextLink href={footerLinks.privacyPolicy} variant="muted" data-testid="agents-footer-privacy">
              Privacy
            </TextLink>
            <TextLink href={footerLinks.serviceStatus} variant="muted" data-testid="agents-footer-status">
              Status
            </TextLink>
          </nav>
        </div>
      </footer>
    </div>
  );
}
